import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Save } from 'lucide-react';
import { readCkScope, useCkScope, scopeKey, Field, Table } from '../components/CkOperationsShared';
import { createReceivingApi } from './api';
import '../components/CkOperations.css';

export default function TemperatureSettings({ userId }) {
  const scope = useCkScope();
  return <TemperatureWorkspace key={`${userId}:${scopeKey(scope)}`} userId={userId} scope={scope} />;
}

export function TemperatureWorkspace({ userId, scope }) {
  const alive = useRef(false);
  const lock = useRef(false);
  const [rules, setRules] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [conflict, setConflict] = useState(false);
  const [selected, setSelected] = useState('');
  const [minimum, setMinimum] = useState('');
  const [maximum, setMaximum] = useState('');
  const [reason, setReason] = useState('');
  const api = useMemo(() => createReceivingApi(() => ({ ...readCkScope(), userId })), [userId]);
  const active = useCallback(() => alive.current && scopeKey(readCkScope()) === scopeKey(scope), [scope]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async () => {
    if (lock.current || !active()) return;
    lock.current = true; setBusy(true); setLoaded(false); setError(''); setCanManage(false);
    setRules([]); setIngredients([]); setSelected(''); setMinimum(''); setMaximum(''); setReason('');
    try {
      const caps = await api.capabilities();
      const rows = [];
      let after;
      do {
        const page = await api.temperatureRules({ after });
        if (!active()) return;
        rows.push(...page.items);
        after = page.next_cursor;
      } while (after != null);
      const items = [];
      if (caps.can_manage) {
        after = undefined;
        do {
          const page = await api.ingredients({ after });
          if (!active()) return;
          items.push(...page.items);
          after = page.next_cursor;
        } while (after != null);
      }
      if (!active()) return;
      setRules(rows); setIngredients(items); setCanManage(caps.can_manage === true);
      setLoaded(true); setConflict(false);
    } catch (e) { if (active()) setError(e.message); }
    finally { lock.current = false; if (active()) setBusy(false); }
  }, [api, active]);
  useEffect(() => { load(); }, [load]);

  function select(id) {
    const rule = rules.find(row => row.ingredient_id === id);
    setSelected(id); setMinimum(rule?.minimum_c ?? ''); setMaximum(rule?.maximum_c ?? '');
    setReason(''); setNotice('');
  }

  async function save(event) {
    event.preventDefault();
    if (lock.current || !active() || !canManage || conflict) return;
    if (!selected || !reason.trim() || (!minimum.trim() && !maximum.trim())) {
      setError('Select an ingredient, enter a temperature limit and give a change reason.'); return;
    }
    lock.current = true; setBusy(true); setError(''); setNotice('');
    try {
      const current = rules.find(row => row.ingredient_id === selected);
      const saved = await api.saveTemperatureRule(selected, {
        version: current?.version ?? 0, minimum_c: minimum.trim() || null,
        maximum_c: maximum.trim() || null, reason: reason.trim(),
      });
      if (!active()) return;
      setRules(previous => [...previous.filter(row => row.ingredient_id !== selected), saved]);
      setMinimum(saved.minimum_c ?? ''); setMaximum(saved.maximum_c ?? ''); setReason('');
      setNotice('Temperature rule saved.');
    } catch (e) {
      if (!active()) return;
      if (e.status === 409) { setConflict(true); setError('This rule changed. Reload the current rules before editing again.'); }
      else { setError(e.message); if ([401, 403].includes(e.status)) { setCanManage(false); setRules([]); } }
    } finally { lock.current = false; if (active()) setBusy(false); }
  }

  const name = id => ingredients.find(item => item.id === id)?.name || id;
  return <main className="ck-ops" aria-label="Receiving temperature settings">
    <header><h1>Receiving temperatures</h1><button title="Reload rules" aria-label="Reload rules" onClick={load} disabled={busy}><RefreshCw size={18} /></button></header>
    {error && <p role="alert" className="ck-error">{error}</p>}
    {notice && <p role="status" className="ck-notice">{notice}</p>}
    {busy && <p role="status">Loading or saving rules...</p>}
    {loaded && <>
      {!canManage && <p>Read-only access</p>}
      <Table headers={['Ingredient', 'Minimum C', 'Maximum C', 'Version', 'Change reason']} empty={!rules.length}>
        {rules.map(rule => <tr key={rule.ingredient_id}>
          <td>{canManage ? <button disabled={busy || conflict} onClick={() => select(rule.ingredient_id)}>{name(rule.ingredient_id)}</button> : name(rule.ingredient_id)}</td>
          <td>{rule.minimum_c ?? 'Not set'}</td><td>{rule.maximum_c ?? 'Not set'}</td>
          <td>{rule.version}</td><td>{rule.reason}</td>
        </tr>)}
      </Table>
      {canManage && <form className="ck-form" aria-label="Temperature rule" onSubmit={save}>
        <h2>Site rule</h2><fieldset disabled={busy || conflict}><div className="ck-fields">
          <Field label="Ingredient" value={selected} onChange={e => select(e.target.value)} required
            options={[{ value: '', label: 'Select ingredient' }, ...ingredients.map(item => ({ value: item.id, label: item.name || item.id }))]} />
          <Field label="Minimum C" inputMode="decimal" value={minimum} onChange={e => setMinimum(e.target.value)} />
          <Field label="Maximum C" inputMode="decimal" value={maximum} onChange={e => setMaximum(e.target.value)} />
          <Field label="Change reason" maxLength={500} required value={reason} onChange={e => setReason(e.target.value)} />
        </div><button className="ck-primary" type="submit"><Save size={18} />Save rule</button></fieldset>
      </form>}
    </>}
  </main>;
}
